// v2 tiger — a prowling big cat whose stripes are alternate banded parts: thin
// ellipsoid slices hugging the barrel plus angled shoulder and haunch bands and
// a crown band, over a deep-chested body with a white belly, a round head with
// white cheek fluff and muzzle, dark ears, and a long ringed tail with a dark
// tip. Two forms: flame orange with ink stripes and the white tiger with
// smoke-grey stripes. Ported 1:1 from age-of-mcp's v2/tiger.ts, using the
// library's shared `critter` (quadLegs) and `safari` (bands, eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, safari, type Part } from "../../lib";

const COATS = [
  { coat: "#e08530", chest: "#ea9240", belly: "#f2ead8", stripe: "#2e1d12",
    cheek: "#f2ead8", paw: "#f2ead8" },
  { coat: "#e8e7e0", chest: "#f0efe8", belly: "#fafaf4", stripe: "#5e564a",
    cheek: "#fafaf4", paw: "#fafaf4" },
];

function tigerParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.062, dz: 0.03, bodyY: 0.092, rTop: 0.01, rFoot: 0.008,
    color: c.coat, hoof: { color: c.paw, r: 0.0105, h: 0.011 },
  });
  parts.push(
    // deep-chested prowling body with a white belly
    { geometry: new THREE.IcosahedronGeometry(0.052, 0), color: c.coat,
      position: [0, 0.092, 0], scale: [1.85, 0.95, 0.88] },
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.chest,
      position: [0.062, 0.09, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.04, 0), color: c.coat,
      position: [-0.075, 0.092, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.036, 0), color: c.belly,
      position: [0.005, 0.075, 0], scale: [1.5, 0.65, 0.8] },
    // angled shoulder + haunch stripe bands
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.stripe,
      position: [0.058, 0.096, 0], rotation: [0, 0, -0.45],
      scale: [0.13, 0.95, 0.92] },
    { geometry: new THREE.IcosahedronGeometry(0.043, 0), color: c.stripe,
      position: [-0.078, 0.096, 0], rotation: [0, 0, 0.5],
      scale: [0.13, 0.92, 0.95] },
    // round head: crown band, white cheek fluff and muzzle, dark nose, ears
    { geometry: new THREE.IcosahedronGeometry(0.028, 0), color: c.coat,
      position: [0.107, 0.104, 0], scale: [1.05, 0.95, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.0285, 0), color: c.stripe,
      position: [0.098, 0.111, 0], scale: [0.13, 0.6, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.0115, 0), color: c.cheek,
      position: [0.111, 0.09, 0.0225], scale: [0.75, 0.8, 0.5] },
    { geometry: new THREE.IcosahedronGeometry(0.0115, 0), color: c.cheek,
      position: [0.111, 0.09, -0.0225], scale: [0.75, 0.8, 0.5] },
    { geometry: new THREE.IcosahedronGeometry(0.0145, 0), color: c.belly,
      position: [0.13, 0.096, 0], scale: [1.1, 0.78, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.0048, 0), color: "#2a1812",
      position: [0.1445, 0.1015, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.008, 0), color: c.stripe,
      position: [0.0915, 0.128, 0.018] },
    { geometry: new THREE.IcosahedronGeometry(0.008, 0), color: c.stripe,
      position: [0.0915, 0.128, -0.018] },
    // long tail with a stripe ring and the dark tip
    safari.tube([-0.105, 0.1, 0], [-0.148, 0.082, 0.01], 0.0048, 0.0034, c.coat),
    { geometry: new THREE.IcosahedronGeometry(0.0058, 0), color: c.stripe,
      position: [-0.128, 0.092, 0.005] },
    { geometry: new THREE.IcosahedronGeometry(0.0062, 0), color: c.stripe,
      position: [-0.153, 0.08, 0.011] },
  );
  // the body stripes — four slices wrapped over the barrel
  parts.push(...safari.bands({
    xs: [-0.052, -0.016, 0.02, 0.052], y: 0.092, r: 0.0535,
    scale: [0.13, 0.95, 0.9], color: c.stripe,
  }));
  parts.push(...safari.eyes(0.122, 0.111, 0.0125, 0.0038, "#1c1810"));
  return parts;
}

export const tigerV2 = defineModel({
  id: "tiger-v2",
  scale: 2.81, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Tiger (v2)",
  tags: ["creature","tiger","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tiger.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.14,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => tigerParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => tigerParts(1) },
  ],
  animations: [],
});
